import type {
  FindOperationsInput,
  OperationItem,
} from '@travel-planner/shared/schemas/operations';
import { useQuery } from '@tanstack/react-query';
import { ATTENTION_RED_FLAGGED } from '@travel-planner/shared/schemas/operations';
import { trpc } from '@/shared/api/trpc';

const SIXTY_SECONDS = 60 * 1000;

// The shared `operations.list` schema caps a page at 100 rows. The Notification
// modal needs an itinerary's WHOLE red-flag set, and a single operations page
// can span itineraries whose combined red flags exceed 100 (100 items × up to
// N flags each), so a single `limit: 100` request is NOT guaranteed complete —
// the loop below pages through `total`.
const RED_FLAG_NOTES_PAGE_SIZE = 100;
// ponytail: 200-page ceiling (20k red flags for one page's itineraries) is a
// runaway backstop, orders of magnitude above any real data; if it ever trips,
// the itinerary set is pathological and a dedicated slim endpoint is warranted.
const MAX_RED_FLAG_PAGES = 200;

// Input for the dedicated red-flag-history query that backs the Notification
// modal. Mirrors legacy V2 (`attentionCategory` + `skipFilters`): deliberately
// independent of the main operations list —
//  - `attentionCategory: ATTENTION_RED_FLAGGED` — only red-flagged items;
//  - `skipDateRangeFilter: true` — ignore the [today, today+7] window so OLDER
//    flags still surface;
//  - `itineraries` — scoped to the itineraries visible on the current page
//    (indexed lookup, so paging is cheap);
//  - `offset` — walked by the fetch-all loop.
export const buildRedFlagNotesInput = (
  itineraryIds: readonly string[],
  offset = 0
): FindOperationsInput => ({
  attentionCategory: ATTENTION_RED_FLAGGED,
  skipDateRangeFilter: true,
  itineraries: [...itineraryIds],
  offset,
  limit: RED_FLAG_NOTES_PAGE_SIZE,
  view: 'active',
});

// Fetch only when the page actually has itineraries. No surface / `flagged`
// bullet dependency (unlike `useFlaggedItemsData`): the modal needs complete
// history on the Home tab too, so a non-empty id set is the sole condition.
export const shouldFetchRedFlagNotes = (
  itineraryIds: readonly string[]
): boolean => itineraryIds.length > 0;

type RedFlagPage = { data: OperationItem[]; total: number };

// Walk every page of the scoped red-flag query and merge into one list,
// deduped by item id (a concurrent write shifting the window could otherwise
// repeat a row across pages). Stops at `total`, an empty page, or the runaway
// backstop. Pure over the injected `fetchPage`, so the >100 case is unit
// testable without a live server.
export const fetchAllRedFlagPages = async (
  fetchPage: (offset: number) => Promise<RedFlagPage>
): Promise<OperationItem[]> => {
  const all: OperationItem[] = [];
  const seen = new Set<string>();
  let offset = 0;
  for (let page = 0; page < MAX_RED_FLAG_PAGES; page++) {
    const { data, total } = await fetchPage(offset);
    for (const item of data) {
      if (item.id && !seen.has(item.id)) {
        seen.add(item.id);
        all.push(item);
      }
    }
    offset += data.length;
    if (data.length === 0 || offset >= total) break;
  }
  return all;
};

// Complete red-flag history for the itineraries on the current page. The query
// key shares the `operations.list` prefix so the argless
// `utils.operations.list.invalidate()` fired after an attention-note edit
// refetches this too. Keyed by the (sorted, deduped) id set so an unchanged set
// reuses the cache; no `placeholderData`, so between itinerary sets `data` is
// `undefined` (loading) rather than a previous page's rows.
export const useRedFlagNotesData = (itineraryIds: readonly string[]) => {
  const utils = trpc.useUtils();
  const ids = [...new Set(itineraryIds.filter(Boolean))].sort();
  return useQuery<OperationItem[]>({
    queryKey: [['operations', 'list'], 'red-flag-notes', ids],
    enabled: shouldFetchRedFlagNotes(ids),
    staleTime: SIXTY_SECONDS,
    queryFn: () =>
      fetchAllRedFlagPages((offset) =>
        utils.operations.list.fetch(buildRedFlagNotesInput(ids, offset))
      ),
  });
};
